import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Sparkles, CheckCircle2, Clock, ShieldCheck, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n, cur) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
const hours = (h) => (h < 48 ? `${h} horas` : `${h / 24} días`);

const NotFound = () => (
  <div className="min-h-screen bg-white text-black flex flex-col items-center justify-center px-6" style={{ fontFamily: "Times New Roman, serif" }} data-testid="opportunities-not-found">
    <h1 className="text-2xl font-bold mb-3">404 Not Found</h1>
    <hr className="w-full max-w-lg border-neutral-300 mb-3" />
    <p className="text-xs text-neutral-500 italic">nginx</p>
  </div>
);

/** Public page: the client picks one of the 3 liquidation opportunities. */
export default function OpportunitiesPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/opportunities/${token}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.status === 404 ? "notfound" : (e?.response?.data?.detail || "No se pudo cargar"));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const confirm = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/opportunities/${token}/choose`, { option_id: selected.id });
      setData(r.data);
      setConfirming(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo confirmar");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  if (error === "notfound") return <NotFound />;

  return (
    <div className="min-h-screen bg-[#050308] text-white px-4 py-10 relative overflow-hidden" data-testid="opportunities-page">
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(ellipse at 20% 0%, rgba(217,70,239,0.25), transparent 55%), radial-gradient(ellipse at 90% 100%, rgba(212,175,55,0.2), transparent 50%)" }} />
      <div className="relative max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#d4af37] mb-3"><Sparkles className="w-3.5 h-3.5" /> CrisAngel Astral</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white">Tus oportunidades de liquidación</h1>
        </div>

        {!data && !error && <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-300 mx-auto" /></div>}
        {error && error !== "notfound" && (
          <div data-testid="opportunities-error" className="mb-4 rounded-xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm text-rose-100 flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5" /> {error}</div>
        )}

        {data && (
          <>
            <div className="rounded-2xl border border-white/10 bg-black/50 backdrop-blur p-5 mb-6 text-sm">
              <div className="text-neutral-400">Hola <b className="text-white">{data.client_name}</b>, este es tu trabajo:</div>
              <div className="text-white font-semibold text-lg mt-1">{data.service_name}</div>
              <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                <div><div className="text-neutral-500 uppercase tracking-widest text-[9px]">Total</div><div className="text-white">{money(data.agreed_price, data.currency)}</div></div>
                <div><div className="text-neutral-500 uppercase tracking-widest text-[9px]">Pagado</div><div className="text-emerald-300">{money(data.paid_total, data.currency)}</div></div>
                <div><div className="text-neutral-500 uppercase tracking-widest text-[9px]">Saldo</div><div className="text-amber-300 font-semibold" data-testid="opportunities-remaining">{money(data.remaining, data.currency)}</div></div>
              </div>
            </div>

            {data.status === "chosen" && data.chosen_option && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="opportunity-confirmed" className="rounded-2xl border-2 border-emerald-400/60 bg-emerald-500/10 p-6">
                <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-6 h-6 text-emerald-300" /><h2 className="font-serif text-2xl text-white">¡Promoción confirmada!</h2></div>
                <p className="text-sm text-neutral-300 mb-4">Elegiste <b className="text-white">{data.chosen_option.emoji} {data.chosen_option.label}</b>. Estas son las características de tu promoción:</p>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg bg-black/40 p-3"><div className="text-[9px] uppercase tracking-widest text-neutral-500">Descuento</div><div className="text-fuchsia-300 font-bold text-xl">{data.chosen_option.pct}%</div><div className="text-neutral-400 text-xs">ahorras {money(data.promotion?.discount_amount, data.currency)}</div></div>
                  <div className="rounded-lg bg-black/40 p-3"><div className="text-[9px] uppercase tracking-widest text-neutral-500">Nuevo total a pagar</div><div className="text-emerald-300 font-bold text-xl" data-testid="opportunity-new-total">{money(data.promotion?.new_remaining, data.currency)}</div><div className="text-neutral-400 text-xs">antes {money(data.promotion?.remaining_before, data.currency)}</div></div>
                  <div className="rounded-lg bg-black/40 p-3 sm:col-span-2"><div className="text-[9px] uppercase tracking-widest text-neutral-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Vigencia</div><div className="text-white font-semibold" data-testid="opportunity-valid-until">Hasta {data.promotion_valid_until_human}</div></div>
                </div>
                <ul className="mt-4 space-y-1 text-xs text-neutral-400">
                  {data.rules.map((r, i) => <li key={i} className="flex gap-2"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /> {r}</li>)}
                </ul>
                <p className="mt-4 text-xs text-neutral-500">Te enviamos la confirmación con la imagen de tu promoción a tu chat y a tu WhatsApp. ✨</p>
              </motion.div>
            )}

            {data.status === "sent" && (
              <>
                <div className="text-center text-xs text-neutral-400 mb-4 flex items-center justify-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-amber-300" /> Puedes elegir <b className="text-white">una</b> oportunidad hasta el {data.expires_at_human}
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {data.options.map((o, i) => {
                    const sel = selected?.id === o.id;
                    return (
                      <motion.button
                        key={o.id}
                        type="button"
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                        data-testid={`opportunity-choose-${o.key}`}
                        onClick={() => setSelected(o)}
                        className={`text-left rounded-2xl border-2 p-4 transition ${sel ? "border-fuchsia-400 bg-fuchsia-500/15 shadow-[0_0_40px_rgba(217,70,239,0.3)]" : "border-white/10 bg-black/50 hover:border-fuchsia-400/50"}`}
                      >
                        <div className="text-3xl mb-1">{o.emoji}</div>
                        <div className="text-white font-serif text-xl">{o.label}</div>
                        <div className="text-fuchsia-300 font-bold text-3xl mt-1">{o.pct}%</div>
                        <div className="text-[11px] text-neutral-400 mt-1">{o.tagline}</div>
                        <div className="mt-3 text-xs space-y-0.5">
                          <div className="text-neutral-400">Pagas <b className="text-emerald-300">{money(o.new_remaining, data.currency)}</b></div>
                          <div className="text-neutral-500">Ahorras {money(o.discount_amount, data.currency)}</div>
                          <div className="text-amber-300 flex items-center gap-1"><Clock className="w-3 h-3" /> Liquida en {hours(o.valid_hours)}</div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  data-testid="opportunity-continue"
                  disabled={!selected}
                  onClick={() => setConfirming(true)}
                  className="mt-6 w-full py-3.5 rounded-full bg-[#d4af37] hover:bg-[#f3d878] text-black font-bold uppercase tracking-widest text-xs disabled:opacity-40"
                >
                  {selected ? `Elegir ${selected.emoji} ${selected.label}` : "Selecciona una oportunidad"}
                </button>
                <ul className="mt-5 space-y-1 text-[11px] text-neutral-500">
                  {data.rules.map((r, i) => <li key={i}>• {r}</li>)}
                </ul>
              </>
            )}

            {(data.status === "expired" || data.status === "cancelled" || data.status === "closed") && (
              <div data-testid="opportunities-closed" className="rounded-2xl border border-white/10 bg-black/50 p-6 text-center text-sm text-neutral-300">
                {data.status === "closed" ? "Este trabajo ya está liquidado. ¡Gracias por tu confianza! ✨" : "Estas oportunidades ya no están disponibles. Si deseas una nueva propuesta, escríbeme por el chat. ✨"}
              </div>
            )}
          </>
        )}
      </div>

      {confirming && selected && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center px-4" data-testid="opportunity-confirm-dialog" onClick={() => setConfirming(false)}>
          <div className="w-full max-w-md rounded-2xl border-2 border-fuchsia-400/60 bg-[#0a0510] p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-white mb-2">Confirmar {selected.emoji} {selected.label}</h3>
            <div className="text-sm text-neutral-300 space-y-1 mb-4">
              <div>Descuento: <b className="text-fuchsia-300">{selected.pct}%</b> (ahorras {money(selected.discount_amount, data.currency)})</div>
              <div>Nuevo total a pagar: <b className="text-emerald-300">{money(selected.new_remaining, data.currency)}</b></div>
              <div>Vigencia: <b className="text-white">{hours(selected.valid_hours)}</b> a partir de ahora</div>
            </div>
            <p className="text-[11px] text-neutral-500 mb-4">Al confirmar se activa tu promoción y no podrás cambiar de opción.</p>
            <div className="flex gap-2">
              <button data-testid="opportunity-confirm-cancel" onClick={() => setConfirming(false)} className="flex-1 py-2.5 rounded-full border border-white/15 text-neutral-300 text-xs uppercase tracking-widest">Volver</button>
              <button data-testid="opportunity-confirm" disabled={busy} onClick={confirm} className="flex-1 py-2.5 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-1.5 disabled:opacity-50">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
